import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronLeft, ChevronRight } from '../../icons';
import { Badge, Button, PageHeader, Table, type TableColumn } from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  getLeaveSummary, listLeaves, listBusinessTrips, listOvertime,
  type LeaveSummaryDto, type LeaveDto, type TripDto, type OvertimeDto,
} from '../../api/hr';
import { fetchMyWage, hoursText, touchesNight, type MyWageDto, type MyWageSpanDto } from '../../api/hr-my-wage';
import { formatWon } from '../../api/hr-wage';
import { leaveAmountText, leaveTotalText } from '../../lib/leave-amount';
import { leaveTimeRangeText } from '../../lib/partial-leave-time';
import { leaveAvailable } from '../../lib/leave-balance';
// 「9시간」·「1시간 30분」 — 근태가 시간을 적는 한 가지 방법 (판 B 에서 쓰기 시작).
import { formatWorkMinutes } from '../../lib/daily-work';
// 내 대체휴가 (2026-09-06 · 판 C) — 줄마다 기한이 다르다.
import { listMySubstituteCredits, type MySubstituteDto, type SubstituteCreditDto } from '../../api/hr-substitute';
// 3개 언어 문구는 보존해 둔 화면이 이미 갖고 있다 — 27개 문자열을 다시 쓰지 않는다.
import { tHR } from '../HRPage/HRPage';
import { PremiumOverlapChart } from './AttendanceCharts';
// 「근무 내역」이 간 자리 — 이 화면은 **어디로 갔는지만** 적는다.
import { ATT_HOME_PATH } from '../../lib/att-menu';
// 공용 모듈은 **제 CSS 를 데리고 다닌다** — 부모가 들여오는 화면에서만 먹는 이름이 되면 안 된다.
import '../HrMenu/HrShell.css';
import './MyAttendanceHistory.css';

/**
 * 내 근태 내역 — 달마다 보는 것 (2026-09-03 · 정본 P3-3).
 *
 * ## 왜 생겼나
 *
 * 「내 휴가 · 출장 · 잔업」 과 「내 임금 · 가산 시간」 두 화면을 합친 것이다. 둘은 **같은 한 달**을
 * 말하면서 겹치는 자리가 다섯이었다 — 그중 하나(가산 시간)는 **서버의 같은 함수 하나**
 * (`splitPremium()`)를 두 화면이 각각 읽어 이름만 달리 적고 있었고, 「넷을 더하지 마세요」라는
 * **같은 설명문도 두 벌**이었다. 한쪽만 고쳐지면 두 화면이 다른 말을 하게 되는 상태였다.
 *
 * ## 무엇이 달라졌나
 *
 * · **달 이동이 화면 머리로** 간다 — 아래 모든 절이 그 한 달을 말하기 때문이다.
 * · **가산 시간은 여기 한 번만** 있다(「내 근태」에서 걷어냈다).
 * · 휴가 · 출장 · 잔업 세 표가 **「종류」 칸을 더해 한 표**가 된다 — 「내가 낸 신청이 어떻게 됐나」는
 *   한 가지 물음이지 세 가지가 아니다.
 * · **새 신청은 여기서 내지 않는다** — 「내 근태」의 신청 줄이 그 일을 한다. 여기는 결과를 본다.
 *
 * ★ 연차는 **해 단위**라 달을 바꿔도 안 움직인다 — 그래서 절 이름에 연도를 적는다.
 * ★ 못 읽으면 그 절만 접는다(옛 서버·권한 어느 쪽이든).
 */

/** 상태 알약 — 색만으로 뜻을 만들지 않는다(정본 P5). 알약 안에 낱말이 함께 적힌다. */
const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger'> = {
  APPROVED: 'success', PENDING: 'warning', REJECTED: 'danger', CANCELED: 'danger',
};

/** 세 갈래를 한 표로 모은 줄. */
interface RequestRow {
  key: string;
  kind: string;
  content: string;
  contentSub?: string;
  period: string;
  periodSub?: string;
  length: string;
  status: string;
  /** 최신순으로 세우기 위한 날짜(YYYY-MM-DD). */
  at: string;
}

const ymOf = (d: Date) => ({ year: d.getFullYear(), month: d.getMonth() + 1 });
const shift = (year: number, month: number, by: number) => ymOf(new Date(year, month - 1 + by, 1));

/**
 * 「무엇으로 쌓였나」 — 「휴일근로」 · 「연장근로」 · 「휴일근로 + 야간」 (2026-09-06 · 판 C).
 *
 * ★ **0 인 갈래는 안 적는다.** 회사가 그 갈래를 껐거나 그날 없었던 것이고, 둘 다
 *   「그 시간은 안 바뀌었다」는 뜻이라 적으면 거짓이 된다(신청 팝업과 같은 규칙).
 */
export function substituteSourceText(c: {
  overtimeMinutes?: number; holidayMinutes?: number; holidayOverMinutes?: number; nightMinutes?: number;
}): string {
  const parts: string[] = [];
  if ((c.holidayMinutes ?? 0) > 0 || (c.holidayOverMinutes ?? 0) > 0) parts.push('휴일근로');
  if ((c.overtimeMinutes ?? 0) > 0) parts.push('연장근로');
  if ((c.nightMinutes ?? 0) > 0) parts.push('야간');
  return parts.length > 0 ? parts.join(' + ') : '가산 근로';
}

export function MyAttendanceHistoryPage() {
  const t = tHR();
  const [at, setAt] = useState(() => ymOf(new Date()));

  // 해 단위 — 달을 바꿔도 다시 부르지 않는다.
  const [summary, setSummary] = useState<LeaveSummaryDto | null>(null);
  const [leaves, setLeaves] = useState<LeaveDto[]>([]);
  const [trips, setTrips] = useState<TripDto[]>([]);
  const [overtime, setOvertime] = useState<OvertimeDto[]>([]);
  const [leaveState, setLeaveState] = useState<'loading' | 'ok' | 'folded'>('loading');

  // 달 단위.
  const [wage, setWage] = useState<MyWageDto | null>(null);
  const [wageState, setWageState] = useState<'loading' | 'ok' | 'folded'>('loading');

  /**
   * 내 대체휴가 (2026-09-06 · 판 C) — **언제 · 무엇으로 쌓였고 언제까지 쓰나**.
   *
   * ★★★ 종전에는 「대체휴가 잔여 1일」 한 줄뿐이었다. 그런데 이 휴가는 **줄마다 기한이 다르고**
   *    기한을 넘기면 사라진다 — 「언제까지」를 모르면 **쓸 수 있었던 휴가를 잃는다.**
   * ★ 정책이 꺼진 회사(지금 전부)·옛 서버면 목록이 비어 **절이 통째로 안 뜬다**.
   */
  const [subs, setSubs] = useState<MySubstituteDto | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, l, tr, ot] = await Promise.all([
          getLeaveSummary(), listLeaves(), listBusinessTrips(), listOvertime(),
        ]);
        if (!alive) return;
        setSummary(s); setLeaves(l); setTrips(tr); setOvertime(ot);
        setLeaveState('ok');
      } catch {
        if (alive) setLeaveState('folded');
      }
    })();
    return () => { alive = false; };
  }, []);

  // ★ 실패(404 옛 서버 · 네트워크)는 조용히 넘긴다 — 이 절만 사라진다.
  useEffect(() => {
    let alive = true;
    listMySubstituteCredits()
      .then((r) => { if (alive) setSubs(r); })
      .catch(() => { if (alive) setSubs(null); });
    return () => { alive = false; };
  }, []);

  const loadWage = useCallback(async (year: number, month: number) => {
    setWageState('loading');
    try {
      setWage(await fetchMyWage(year, month));
      setWageState('ok');
    } catch {
      setWageState('folded');
    }
  }, []);
  useEffect(() => { void loadWage(at.year, at.month); }, [at.year, at.month, loadWage]);

  /**
   * 내 대체휴가 표 (2026-09-06 · 판 C) — **언제 · 무엇으로 · 언제까지**.
   *
   * ★★ 「남음」이 `null` 인 줄은 **«모른다»** 다(승인 아닌 줄은 배분에 안 들어간다) —
   *    0 으로 그리면 「다 썼다」로 읽힌다.
   */
  const substituteColumns: ReadonlyArray<TableColumn<SubstituteCreditDto>> = useMemo(() => [
    { key: 'date', header: '일한 날', width: '130px', rowHeader: true,
      render: (c) => <span className="myhist__date">{c.workDate}</span> },
    { key: 'src', header: '무엇으로', width: '160px', render: (c) => substituteSourceText(c) },
    { key: 'credit', header: '적립', width: '110px', render: (c) => c.creditText },
    {
      key: 'left',
      header: '남음',
      width: '110px',
      render: (c) => (c.leftMinutes == null
        ? <span className="hrpage__dim">—</span>
        : formatWorkMinutes(c.leftMinutes)),
    },
    {
      key: 'until',
      header: '언제까지',
      width: '130px',
      render: (c) => (c.expiresAt
        ? <span className={c.expired ? 'is-warn' : 'myhist__date'}>{c.expiresAt}</span>
        : <span className="hrpage__dim">기한 없음</span>),
    },
    {
      key: 'state',
      header: '상태',
      width: '110px',
      render: (c) => (c.expired
        ? <Badge variant="warning" size="sm">기한 지남</Badge>
        : c.status === 'APPROVED' ? <Badge variant="info" size="sm">쓸 수 있음</Badge>
          : c.status === 'PENDING' ? <Badge variant="warning" size="sm">결재 중</Badge>
            : <Badge size="sm">{c.status === 'REJECTED' ? '반려' : '취소'}</Badge>),
    },
  ], []);

  const statusLabel = (s: string) => (t.leave_status as Record<string, string>)[s] ?? s;
  const typeLabel = (ty: string) => (t.leave_type as Record<string, string>)[ty] ?? ty;
  const daily = summary?.dailyMinutes;
  const maxMonthly = Math.max(1, ...(summary?.monthlyUsage ?? [0]));

  /**
   * 세 갈래를 한 표로 — **「종류」 칸이 가른다.**
   * ★ 아무것도 버리지 않는다. 출장의 목적지 · 잔업의 사유는 「내용」 으로, 각각의 곁말은
   *   그 아래 흐린 줄로 남는다(합칠 때는 «많이 말하는 쪽» 으로 모은다).
   */
  const requests: RequestRow[] = useMemo(() => {
    const rows: RequestRow[] = [
      ...leaves.map((r) => ({
        key: `l-${r.id}`,
        kind: typeLabel(r.type),
        content: r.reason || '—',
        period: r.startDate,
        // S6 — 반차 · 반반차는 하루 안이라 끝 날짜 대신 구간(옛 기록은 종전 그대로).
        periodSub: r.endDate !== r.startDate ? `~ ${r.endDate}` : (leaveTimeRangeText(r) ?? undefined),
        length: leaveAmountText(r, daily),
        status: r.status,
        at: r.startDate,
      })),
      ...trips.map((r) => ({
        key: `t-${r.id}`,
        kind: t.section_trips,
        content: r.destination,
        contentSub: r.purpose || undefined,
        period: r.startDate,
        periodSub: r.endDate !== r.startDate ? `~ ${r.endDate}` : undefined,
        length: r.startDate === r.endDate ? '1일' : '',
        status: r.status,
        at: r.startDate,
      })),
      ...overtime.map((r) => ({
        key: `o-${r.id}`,
        kind: t.section_overtime,
        content: r.reason || '—',
        period: r.date,
        periodSub: r.startTime && r.endTime ? `${r.startTime}–${r.endTime}` : undefined,
        length: `${r.hours}${t.unit_hours}`,
        status: r.status,
        at: r.date,
      })),
    ];
    return rows.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  }, [leaves, trips, overtime, daily, t]);

  const requestColumns: ReadonlyArray<TableColumn<RequestRow>> = [
    { key: 'kind', header: '종류', width: '104px', render: (r) => <Badge variant="info" size="sm">{r.kind}</Badge> },
    {
      key: 'content', header: '내용',
      render: (r) => (
        <>
          {r.content}
          {r.contentSub ? <span className="hrpage__dim"> · {r.contentSub}</span> : null}
        </>
      ),
    },
    {
      key: 'period', header: t.col_period, width: '206px',
      render: (r) => (
        <>
          <span className="myhist__date">{r.period}</span>
          {r.periodSub ? <span className="hrpage__dim"> {r.periodSub}</span> : null}
        </>
      ),
    },
    { key: 'length', header: '길이', numeric: true, width: '88px' },
    {
      key: 'status', header: t.col_status, align: 'right', width: '78px',
      render: (r) => <Badge variant={STATUS_TONE[r.status] ?? 'default'} size="sm">{statusLabel(r.status)}</Badge>,
    },
  ];

  const spanColumns: ReadonlyArray<TableColumn<MyWageSpanDto>> = [
    { key: 'date', header: '날짜', width: '76px', render: (s) => <span className="hrwage__date">{s.date.slice(5)}</span> },
    {
      key: 'kind', header: '구분', width: '116px',
      render: (s) => (
        <span className="hrwage__kind">
          <Badge variant={s.holiday ? 'danger' : 'info'} size="sm">{s.holiday ? '휴일' : '연장'}</Badge>
          {touchesNight(s.start, s.end) && <Badge variant="warning" size="sm">야간</Badge>}
        </span>
      ),
    },
    { key: 'span', header: '시간', render: (s) => <span className="hrpage__dim">{s.start} – {s.end}</span> },
    { key: 'minutes', header: '실 시간', numeric: true, render: (s) => hoursText(s.minutes) },
  ];

  const p = wage?.premium;
  /**
   * 겹침 띠에 그릴 하루 — **야간에 실제로 걸친 첫 건**만 고른다.
   * ★ 겹치는 건이 없으면 **그리지 않는다**. 겹치지 않는데 겹친다고 그리면 그림이 거짓말을 한다.
   */
  const overlapSpan = useMemo(
    () => (wage?.spans ?? []).find((s) => touchesNight(s.start, s.end)) ?? null,
    [wage],
  );

  return (
    <div className="hrpage myhist">
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/me')}
        scope={scopeFor('/erp/attendance/me')}
        title="내 근태 내역"
        description="연차 · 가산 시간 · 낸 신청 · 임금 — 실제 지급은 급여 명세가 정합니다."
        actions={(
          <div className="myhist__month">
            <Button
              variant="ghost" size="compact" iconOnly aria-label="이전 달"
              onClick={() => setAt(shift(at.year, at.month, -1))}
            >
              <ChevronLeft size={16} aria-hidden />
            </Button>
            <b>{at.year}년 {at.month}월</b>
            <Button
              variant="ghost" size="compact" iconOnly aria-label="다음 달"
              onClick={() => setAt(shift(at.year, at.month, 1))}
            >
              <ChevronRight size={16} aria-hidden />
            </Button>
          </div>
        )}
      />

      {/*
        ★★★ **「근무 내역」은 「내 근태」로 갔다** (2026-09-06 · 사용자 지시).
           판 B 가 이 화면에 얹었던 주간 · 월간 절이다 — 자리를 잘못 잡았다.
           하루 단위로 「어느 날 몇 시에 왔다 갔나」를 보는 것은 **매일 여는 화면**의 일이고,
           여기는 **달마다 보는 것**(연차 · 가산 · 낸 신청 · 임금)을 담는다.
        ★★ **「없어졌다」와 「옮겼다」는 다른 말이다** — 이 줄이 어디로 갔는지 적는다.
           안 적으면 어제까지 여기서 보던 사람은 사라진 줄 안다.
      */}
      <p className="hrpage__note">
        <b>근무 내역</b>(주간 · 월간 · 하루 펼쳐 보기)은 <Link to={ATT_HOME_PATH}>내 근태</Link>로 옮겼습니다.
      </p>

      {/* ── 연차 — 해 단위라 달을 바꿔도 안 움직인다 ── */}
      {leaveState === 'loading' && <div className="hrpage__note">{t.loading}</div>}
      {leaveState === 'folded' && <div className="hrpage__note">{t.err_fetch}</div>}
      {leaveState === 'ok' && summary && (
        <section className="hrpage__panel">
          <h2 className="hrpage__panelt">
            연차 <span className="hrpage__panels">{summary.year}{t.year_suffix} · 해 단위입니다</span>
          </h2>
          <div className="hrpage__kpis">
            <div className="hrpage__kpi">
              <span className="hrpage__kpil">{t.card_total}</span>
              <span className="hrpage__kpiv">
                {leaveTotalText(summary.annual.total ?? 0, summary.annualMinutes?.total ?? undefined, daily)}
              </span>
              <span className="hrpage__kpid">{summary.year}{t.year_suffix}</span>
            </div>
            <div className="hrpage__kpi">
              <span className="hrpage__kpil">{t.card_used}</span>
              <span className="hrpage__kpiv">
                {leaveTotalText(summary.annual.used, summary.annualMinutes?.used, daily)}
              </span>
              <span className="hrpage__kpid">&nbsp;</span>
            </div>
            <div className={`hrpage__kpi${summary.annual.pending > 0 ? ' is-warn' : ''}`}>
              <span className="hrpage__kpil">{t.card_pending}</span>
              <span className="hrpage__kpiv">
                {leaveTotalText(summary.annual.pending, summary.annualMinutes?.pending, daily)}
              </span>
              <span className="hrpage__kpid">결재를 기다리는 몫</span>
            </div>
            <div className="hrpage__kpi is-acc">
              <span className="hrpage__kpil">{t.card_remaining}</span>
              <span className="hrpage__kpiv">
                {leaveTotalText(leaveAvailable(summary.annual) ?? 0, leaveAvailable(summary.annualMinutes) ?? undefined, daily)}
              </span>
              <span className="hrpage__kpid">
                {t.card_sub_remaining}{' '}
                {leaveTotalText(summary.substitute.remaining, summary.substituteMinutes?.remaining, daily)}
              </span>
            </div>
            {/*
              ★★★ **넘겨 썼을 때만 선다** (2026-09-11 · A-1).

                 종전에는 「잔여」가 0 에서 멈춰서, 부여를 다 쓴 사람과 **이틀을 더 쓴 사람**이
                 화면에서 똑같이 「0일」이었다. 둘은 전혀 다른 말이다.

              ★★★ 정본 P7 「셀 수 없는 것은 적지 않는다 · 모르는 값은 0 으로 적지 않는다」 —
                 그래서 **안 넘겼으면(0) · 모르면(null) · 옛 서버면(undefined) 아무 말도 안 한다.**
                 「초과 0일」 칸을 늘 세우면 자리만 차지하고, 없는 일을 물은 것처럼 읽힌다.
              ★ 라벨을 **「연차 초과」**로 적는다 — 이 화면에 「휴일 8시간 초과」가 이미 있어
                그냥 「초과」면 무엇이 넘쳤는지 알 수 없다.
              ★ 색만으로 뜻을 만들지 않는다 — 글자가 스스로 「연차 초과」라고 말한다.
            */}
            {typeof summary.annual.overdrawn === 'number' && summary.annual.overdrawn > 0 && (
              <div className="hrpage__kpi is-bad">
                <span className="hrpage__kpil">연차 초과</span>
                <span className="hrpage__kpiv">
                  {leaveTotalText(
                    summary.annual.overdrawn,
                    summary.annualMinutes?.overdrawn ?? undefined,
                    daily,
                  )}
                </span>
                <span className="hrpage__kpid">부여보다 더 쓴 몫</span>
              </div>
            )}
          </div>

          {/*
            ★★★ **어디서 왔는지 적는다** (2026-09-06 · 판 B). 대체휴가는 이제 출처가 둘이다 —
               휴일대체 신청이 승인돼 **쌓인 것**(자동)과 담당자가 연차 관리에서 **넣은 것**(조정).
               합계만 보이면 「담당자는 0일이라는데 나는 왜 있나」를 아무도 설명할 수 없다.
            ★ 쌓인 것이 없으면 이 줄이 아예 안 뜬다 — 옛 서버·정책이 꺼진 회사가 그렇다.
          */}
          {(summary.substituteMinutes?.credited ?? 0) > 0 && (
            <p className="hrpage__note">
              보상휴가는 <b>승인된 신청으로 쌓인 {formatWorkMinutes(summary.substituteMinutes!.credited!)}</b>
              {(summary.substituteMinutes?.adjusted ?? 0) > 0
                ? <> 과 담당자가 넣은 {formatWorkMinutes(summary.substituteMinutes!.adjusted!)} 을 더한 값입니다.</>
                : <>에서 옵니다.</>}
              {' '}쌓인 몫은 <b>기한</b>이 있고, 그만큼은 예상 수당에서 빠집니다.
            </p>
          )}

          <h3 className="hrpage__panelt myhist__sub">{t.section_monthly}</h3>
          <div className="hrpage__months">
            {summary.monthlyUsage.map((days, idx) => (
              <div className="hrpage__month" key={idx}>
                <span
                  className="hrpage__monthbar"
                  style={{ height: `${days > 0 ? Math.max(6, (days / maxMonthly) * 96) : 2}px` }}
                  aria-hidden
                />
                <span className="hrpage__monthv">{days > 0 ? days : ''}</span>
                <span className="hrpage__monthl">{idx + 1}{t.month_suffix}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/*
        ── 내 대체휴가 (2026-09-06 · 판 C) ──
        ★★★ **줄마다 기한이 다르다.** 합계만 보이면 「언제까지 써야 하나」를 알 수 없고,
           모르면 쓸 수 있었던 휴가를 잃는다. 연차 절 바로 뒤가 맞다 — 같은 「휴가」 이야기이고,
           위 칸의 「대체휴가 잔여」가 어디서 왔는지를 이 표가 풀어 준다.
        ★ 한 줄도 없으면 절이 통째로 안 뜬다(정책이 꺼진 회사 · 옛 서버).
      */}
      {subs && subs.items.length > 0 && (
        <section className="hrpage__panel">
          <h2 className="hrpage__panelt">
            내 보상휴가 <span className="hrpage__panels">근로기준법 §57</span>
          </h2>
          <div className="hrpage__kpis">
            <div className="hrpage__kpi is-acc">
              <span className="hrpage__kpil">지금 쓸 수 있음</span>
              <span className="hrpage__kpiv">
                {subs.liveMinutes != null ? formatWorkMinutes(subs.liveMinutes) : formatWorkMinutes(subs.creditedMinutes)}
              </span>
              <span className="hrpage__kpid">기한 안의 적립분</span>
            </div>
            {/* ★ 0 이면 칸을 안 세운다 — 늘 「0」인 칸은 읽히지 않으면서 자리를 먹는다. */}
            {(subs.expiredMinutes ?? 0) > 0 && (
              <div className="hrpage__kpi is-warn">
                <span className="hrpage__kpil">기한 지남</span>
                <span className="hrpage__kpiv">{formatWorkMinutes(subs.expiredMinutes!)}</span>
                <span className="hrpage__kpid">임금으로 정산됩니다</span>
              </div>
            )}
          </div>
          {/* ★ 이 화면의 다른 표와 **같은 정본 `Table`** 이다 — 한 화면에 표가 두 모양이면 안 된다. */}
          <Table
            caption="내 보상휴가 적립 내역"
            columns={substituteColumns}
            rows={subs.items}
            rowKey={(c) => c.id}
          />
          <p className="hrpage__note">
            보상휴가는 <b>가산까지 넣어</b> 쌓입니다(휴일근로 6시간 → 9시간) — §57 이 「임금 지급을
            <b> 갈음</b>하여」이기 때문입니다. 그만큼은 <b>예상 수당에서 빠집니다</b>.
            <br />
            <b>기한이 지나 못 쓴 몫은 임금으로 돌려받습니다</b> — 사라지지 않습니다.
            {(subs.unmatchedMinutes ?? 0) > 0 && (
              <> 다만 <b>{formatWorkMinutes(subs.unmatchedMinutes!)}</b>는 적립 기록과 맞지 않습니다 —
                근태 담당자에게 확인해 주세요.</>
            )}
          </p>
        </section>
      )}

      {/* ── 가산 시간 — **여기 한 곳에만** 있다 ── */}
      {wageState === 'loading' && <div className="hrpage__note">불러오는 중…</div>}
      {wageState === 'folded' && <div className="hrpage__note">임금 정보를 불러오지 못했습니다.</div>}
      {wageState === 'ok' && wage && p && (
        <>
          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              가산 시간 <span className="hrpage__panels">승인된 신청만 셉니다</span>
            </h2>
            <div className="hrpage__kpis">
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">평일 연장</span>
                <span className="hrpage__kpiv">{hoursText(p.overtimeMinutes)}</span>
                <span className="hrpage__kpid">×1.5</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">야간</span>
                <span className="hrpage__kpiv">{hoursText(p.nightMinutes)}</span>
                <span className="hrpage__kpid">{wage.basis.nightWindow} · +0.5</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">휴일</span>
                <span className="hrpage__kpiv">{hoursText(p.holidayMinutes)}</span>
                <span className="hrpage__kpid">×1.5 · 회사 일정의 휴일</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">휴일 8시간 초과</span>
                <span className="hrpage__kpiv">{hoursText(p.holidayOverMinutes)}</span>
                <span className="hrpage__kpid">×2.0</span>
              </div>
              <div className="hrpage__kpi is-acc">
                <span className="hrpage__kpil">정산 기준 시간</span>
                <span className="hrpage__kpiv">{hoursText(wage.estimate.countedMinutes)}</span>
                {/* ★ 넷을 더한 값이 아니다 — 야간이 연장·휴일과 겹친다. */}
                <span className="hrpage__kpid">겹치는 시간은 한 번만</span>
              </div>
            </div>

            {/* 겹침을 **글 대신 그림**이 말한다. 겹치는 건이 없으면 그리지 않는다. */}
            {overlapSpan && (
              <div className="myhist__overlap">
                <h3 className="hrpage__panelt myhist__sub">
                  왜 넷을 더하면 안 되나{' '}
                  <span className="hrpage__panels">{overlapSpan.date.slice(5)} 하루를 펼친 그림</span>
                </h3>
                <PremiumOverlapChart span={overlapSpan} nightWindow={wage.basis.nightWindow} />
                <div className="attchart-legend">
                  <span><i className="is-credited" /><b>연장</b></span>
                  <span><i className="is-overtime" /><b>야간</b> — 연장 위에 <b>겹쳐서</b> 붙습니다</span>
                </div>
              </div>
            )}

            <p className="hrpage__note myhist__note">
              <b>이 넷을 더하지 마세요</b> — 야간은 연장 · 휴일과 <b>겹칩니다</b>. 시간으로는 한 번만 세고
              가산만 더해 붙습니다(연장 1.5 + 야간 0.5 = 2.0배). 금액은 여기서 만들지 않습니다 —
              근태는 시간까지, 금액은 급여가 셉니다. 휴일은 <b>회사 일정에 있는 날</b>만 셉니다
              (이달 {wage.basis.holidayDates}일)
              {wage.basis.weeklyRestCounted ? '' : ' — 주말(주휴일)은 아직 세지 않습니다'}.
            </p>

            {!wage.wage.registered && (
              <div className="hrasg__overdue" role="status">
                <AlertCircle size={16} aria-hidden />
                임금이 등록되지 않아 시간은 쌓여도 <b>예상 수당이 계산되지 않습니다</b> — 인사 담당자에게 알려 주세요.
              </div>
            )}
          </section>
        </>
      )}

      {/* ── 낸 신청 — 세 갈래를 한 표로 ── */}
      {leaveState === 'ok' && (
        <section className="hrpage__panel" id="myhist-requests">
          <h2 className="hrpage__panelt">
            낸 신청 <span className="hrpage__panels">결재 중인 것도 함께 보입니다</span>
          </h2>
          <Table
            caption="낸 신청 목록"
            columns={requestColumns}
            rows={requests}
            rowKey={(r) => r.key}
            empty="아직 낸 신청이 없습니다."
          />
          <p className="hrpage__note myhist__note">
            「종류」로 가르므로 세 목록을 오가지 않아도 됩니다. <b>새 신청은 「내 근태」의 신청 줄</b>에서
            냅니다 — 여기는 <b>어떻게 됐는지</b>를 보는 자리입니다.
          </p>
        </section>
      )}

      {/* ── 가산 내역 · 임금 ── */}
      {wageState === 'ok' && wage && (
        <div className="hrpage__row">
          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">
              가산 내역 <span className="hrpage__panels">시각까지</span>
            </h2>
            <Table
              caption="가산 내역 목록"
              className="hrwage__tbl"
              columns={spanColumns}
              rows={wage.spans}
              rowKey={(s) => `${s.date}-${s.start}-${s.end}`}
              empty="이 달에 승인된 연장 · 휴일 근로가 없습니다."
            />
          </section>
          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">내 임금 정보</h2>
            {/*
              ★ 이것은 **데이터 표가 아니라 키-값 목록**이다. 정본에 그 패턴이 아직 없어
                옛 표 그대로 둔다 — 없는 패턴을 여기서 지어내지 않는다(질의 → 시안 → 정본 → 코드).
            */}
            <table className="hrpage__tbl">
              <tbody>
                <tr>
                  <td>임금제</td>
                  <td className="hrpage__act">
                    {wage.wage.registered
                      ? <>{wage.wage.wageTypeLabel} <span className="hrpage__pill">등록됨</span></>
                      : <span className="hrpage__dim">등록되지 않음</span>}
                  </td>
                </tr>
                <tr>
                  <td>약정 시간</td>
                  <td className="hrpage__act">
                    {wage.wage.monthlyStandardMinutes !== null
                      ? `${Math.round(wage.wage.monthlyStandardMinutes / 60)}h / 월`
                      : <span className="hrpage__dim">—</span>}
                  </td>
                </tr>
                <tr>
                  <td>통상임금</td>
                  {/* ★ 값이 아니라 «누가 보는가» 를 적는다 — 이 화면에는 오지 않는다. */}
                  <td className="hrpage__act"><span className="hrpage__dim">급여 담당자만 열람</span></td>
                </tr>
                <tr>
                  <td>예상 수당</td>
                  <td className="hrpage__act">
                    {wage.estimate.amount === null
                      ? <span className="hrpage__dim">계산 불가</span>
                      : formatWon(wage.estimate.amount)}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>
      )}
    </div>
  );
}
